import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'

import {
  getDocumentVerificationPath,
  isKycVerified,
  isSumsubSupported,
} from '@utils/AccountAccess/accountKYCStatuses'
import { isEconomicProfileTestNeeded } from '@utils/AccountAccess/accountProductStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  AccountDetailedDto,
  isAccountDetailedClosedStatus,
  isAccountDetailedDormantStatus,
} from '@model/AccountDetailedDto'
import { AccountDetailedStatus } from '@model/AccountDetailedStatus'
import { TickmillProductType } from '@model/TickmillProductType'

import { Loading } from '@shared/components/Loading/Loading'

import styles from './DocumentsPending.module.scss'

interface DocumentsPendingProps {
  className?: string
  hideButton?: boolean
}

export const DocumentsPending: React.FC<DocumentsPendingProps> = (props) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)

  const { data: documents = [], isLoading } = useQuery({
    queryKey: queryKeys.document.required(),
    queryFn: () => apiClient.getRequiredDocuments(),
  })

  const allDocumentsRequired = documents.every((doc) => doc.required)
  const someDocumentsRequired = documents.some((doc) => doc.required)

  const getContent = useMemo(() => {
    if (allDocumentsRequired) {
      return {
        title: t('Documents Required'),
        body: t('Please upload the required documents'),
      }
    } else if (someDocumentsRequired && !allDocumentsRequired) {
      return {
        title: t('Missing Documentation'),
        body: t('Please upload remaining documents'),
      }
    }

    return {
      title: t('Verification Pending'),
      body: t('Documents are currently being reviewed'),
    }
  }, [allDocumentsRequired, someDocumentsRequired, t])

  return (
    <div className={classNames(styles.documentsPending, props.className)}>
      <Loading isLoading={isLoading} showLoadingIcon>
        <Heading6 medium>{getContent.title}</Heading6>
        <Body1 isParagraph className={styles.subtitle} medium>
          {t('Thank you for registering with Tickmill')}
        </Body1>
        <Body1 isParagraph className={styles.text}>
          {getContent.body}
        </Body1>
        {!props.hideButton && (
          <Button.Primary size='md' onClick={() => navigate(getDocumentVerificationPath(account))}>
            {t('Profile.Upload Documents')}
          </Button.Primary>
        )}
      </Loading>
    </div>
  )
}

export const isDocumentPendingVisible = (
  product: TickmillProductType,
  account?: AccountDetailedDto
): boolean => {
  const accountStatus = account?.status.id || -1
  const isAccountActivated = accountStatus === AccountDetailedStatus.Activated
  const isAccountClosed = isAccountDetailedClosedStatus(accountStatus)
  const isAccountDormant = isAccountDetailedDormantStatus(accountStatus)
  const isAPTestFailed = account?.isAppropriatenessTestAccountFailed[product]
  const isEconomicProfileTestPending = isEconomicProfileTestNeeded(account, product)
  const isExpeditedPassed = account?.personalId ? isKycVerified(account, product) : false
  const isSumsub = isSumsubSupported(account)

  return (
    !isAccountActivated &&
    !isAccountClosed &&
    !isAccountDormant &&
    !isAPTestFailed &&
    !isEconomicProfileTestPending &&
    !isSumsub &&
    !isExpeditedPassed
  )
}
